/**
 * Reine Logik der Kennzahlen-Seite (Agentur): Zeiträume, Umsatz, Provision, Agenturgebühren und Creator-Anteil, Aufschlüsselung
 * nach Creator, Marke, Plattform und Monat, offene Forderungen, Pipeline, Buchungen (Ledger) mit Filter und Summen, Formular
 * „Buchung anlegen“. Rechenkern ist computeOverview aus @talentkontor/domain (dieselben Zahlen wie die Übersicht, KZ-01 … KZ-19).
 */
import {
  CURRENCIES, addMonths, bookingCounts, computeOverview, computePipeline, dealOccurrences, goodsValueByTalentYear, invoiceOpenAmountIn, isIsoDate, monthRange, overviewDealFromRow,
  fxMonthConverter, overviewInvoiceFromRow, overviewPeriodFromRow, rowAwaitsDelivery, parseAmountToCents, resolveCommissionRate, dealBpForMonth, type GoodsValueRow, type GoodsValueSummary, type Occurrence, type OverviewFee, type OverviewInput, csvCellGuard,
} from "@talentkontor/domain";
import { decimalSeparator, numberLocale, shortAmount } from "../../../lib/format";
import type { Pill, Tone } from "../../../model/invoice-view";
import { monthNames, t, lazyRecord } from "../../../lib/sprache";

// ── Zeilen aus der Datenbank ───────────────────────────────────────────────

export interface KzTalent {
  id: string;
  first_name: string;
  last_name: string | null;
  display_name: string | null;
  status: string;
  commission_bp: number | null;
}
export interface KzBrand {
  id: string;
  name: string;
  /** Abrechnungsweg der Marke (0039) */
  invoice_route?: string | null;
}
export interface KzDeal {
  id: string;
  deal_number: number | null;
  talent_id: string;
  brand_id: string;
  title: string;
  model: string;
  status: string;
  currency: string;
  budget_cents: number | null;
  commission_bp: number | null;
  platforms: string[] | null;
  archived_at: string | null;
  /** Weg, Staffel und Abzug der Marke (0039, 0060) */
  invoice_route?: string | null;
  commission_tiers?: unknown;
  brand_discount_bp?: number | null;
  brand_discount_bearer?: string | null;
  products_flat_taxed?: boolean;
  /** Nachprovision nach Austritt (0074): Satz je Abrechnungsmonat */
  post_term?: unknown;
  post_term_from?: string | null;
  post_term_until?: string | null;
  /** Herkunft (0074): creator, agency, brand; leer = keine Angabe */
  origin?: string | null;
}
export interface KzPeriod {
  id: string;
  deal_id: string;
  month: string;
  amount_cents: number | null;
  commission_forecast_cents: number | null;
  booked_at: string | null;
  invoice_requested_at: string | null;
  status: string;
  /** Ausgang des Monats (0060) und Ausfallhonorar (0039) */
  outcome?: string | null;
  cancellation_fee_cents?: number | null;
  goods_value_cents?: number | null;
}
export interface KzExtra {
  deal_id: string;
  period_id: string | null;
  amount_cents: number;
  commissionable: boolean;
  created_at?: string | null;
}
export interface KzTerms {
  deal_id: string;
  agency_fee_mode: string | null;
  agency_fee_bp: number | null;
  agency_fee_cents: number | null;
  referral_mode?: string | null;
  referral_bp?: number | null;
  referral_cents?: number | null;
  referral_base?: string | null;
  referral_recipient?: string | null;
}
export interface KzInvoice {
  id: string;
  kind: "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent";
  deal_id: string | null;
  period_id: string | null;
  talent_id: string | null;
  brand_id: string | null;
  number: string | null;
  status: string;
  currency: string;
  gross_cents: number | null;
  net_cents: number | null;
  revenue_month: string | null;
  issue_date: string | null;
  document_due_date: string | null;
  effective_due_date: string | null;
  amount_org_cents: number | null;
  created_at?: string | null;
  /** Zahlungen (Teilzahlung) und Forderungsausfall (0076) */
  paid_cents?: number | null;
  bad_debt_at?: string | null;
  /** Versand und Zugang (GELD-R3-7): ohne Zugang keine Überfälligkeit */
  sent_at?: string | null;
  delivered_at?: string | null;
  credit_for?: string | null;
}
export type BuchungsArtDb = "commission" | "agency_fee" | "usage_rights" | "bonus" | "performance" | "expense" | "correction" | "other";
export interface KzBooking {
  id: string;
  org_id: string;
  deal_id: string | null;
  period_id: string | null;
  talent_id: string | null;
  brand_id: string | null;
  invoice_id: string | null;
  kind: BuchungsArtDb;
  booked_on: string;
  amount_cents: number;
  currency: string;
  fx_rate_to_org: number | null;
  amount_org_cents: number | null;
  talent_share_cents: number | null;
  agency_share_cents: number | null;
  visible_to_talent: boolean;
  status: "planned" | "booked" | "cancelled";
  source: "manual" | "invoice";
  note: string | null;
  created_at: string;
  booked_at: string | null;
}
export interface KzSettings {
  currency: string;
  currencies: string[] | null;
  default_commission_bp: number | null;
  agency_fee_mode: string | null;
  agency_fee_bp: number | null;
  agency_fee_cents: number | null;
  /** Umsatzbeginn (0047): leer oder status = Arbeitsstand, contract_signed = unterschriebener Vertrag */
  revenue_start?: string | null;
  invoice_route?: string | null;
}
export interface KzFx {
  base: string;
  quote: string;
  valid_on: string;
  rate: number;
}

export interface KennzahlenDaten {
  talents: KzTalent[];
  brands: KzBrand[];
  deals: KzDeal[];
  periods: KzPeriod[];
  extras: KzExtra[];
  terms: KzTerms[];
  invoices: KzInvoice[];
  bookings: KzBooking[];
  settings: KzSettings | null;
  fx: KzFx[];
  /** Verträge je Kooperation (Hauptbezug und Rahmenverträge) für den Umsatzbeginn „Vertrag unterschrieben“ (0047) */
  contracts?: { deal_id: string | null; status: string }[];
  contractLinks?: { deal_id: string; contract_id: string }[];
  contractStatus?: { id: string; status: string }[];
  /** Monate von Sammelrechnungen (invoice_periods, 0047) */
  invoicePeriods?: { invoice_id: string; period_id: string }[];
  /** Katalog der Plattformen der Agentur (0047) */
  platforms?: { key: string; label_de: string; label_en: string | null }[];
}

/** Kooperationen mit unterschriebenem Vertrag (Hauptbezug oder weitere Verknüpfung). */
export function unterschriebeneKooperationen(d: Pick<KennzahlenDaten, "contracts" | "contractLinks" | "contractStatus">): Set<string> {
  const out = new Set<string>();
  for (const c of d.contracts ?? []) if (c.deal_id && c.status === "signed") out.add(c.deal_id);
  const signed = new Set((d.contractStatus ?? []).filter((c) => c.status === "signed").map((c) => c.id));
  for (const l of d.contractLinks ?? []) if (signed.has(l.contract_id)) out.add(l.deal_id);
  return out;
}

// ── Anzeige ────────────────────────────────────────────────────────────────

const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
const pill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: SYMBOL[tone] });

/** Rückfall ohne Katalog; Bezeichnungen wie im Katalog der Agentur (Befund 15) */
export const PLATTFORM: Record<string, string> = {
  instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", ugc: "UGC", podcast: "Podcast", twitch: "Twitch", linkedin: "LinkedIn", pinterest: "Pinterest",
  snapchat: "Snapchat", facebook: "Facebook", blog: "Blog", newsletter: "Newsletter", get other() { return t("Weitere", "Other"); }, get manual() { return t("Manuelle Buchungen", "Manual bookings"); },
};
/** Plattform aus dem Katalog der Agentur, sonst Rückfall, sonst Schlüssel. */
export function plattformName(key: string, katalog: KennzahlenDaten["platforms"] = [], en = numberLocale().startsWith("en")): string {
  const e = (katalog ?? []).find((c) => c.key === key);
  if (e) return (en && e.label_en?.trim()) || e.label_de;
  return PLATTFORM[key] ?? key;
}

export const BUCHUNGS_ARTEN: readonly { key: BuchungsArtDb; label: string }[] = [
  { key: "commission", get label() { return t("Provision", "Commission"); } },
  { key: "agency_fee", get label() { return t("Agenturgebühr", "Agency fee"); } },
  { key: "usage_rights", get label() { return t("Nutzungsrechte", "Usage rights"); } },
  { key: "bonus", label: "Bonus" },
  { key: "performance", label: "Performance" },
  { key: "expense", get label() { return t("Auslage", "Expense"); } },
  { key: "correction", get label() { return t("Korrektur oder Gutschrift", "Correction or credit"); } },
  { key: "other", get label() { return t("Sonstiges", "Other"); } },
];
export const BUCHUNGS_ART_LABEL: Record<string, string> = lazyRecord(BUCHUNGS_ARTEN.map((a) => a.key), (k) => BUCHUNGS_ARTEN.find((a) => a.key === k)!.label);

export function talentName(t: Pick<KzTalent, "display_name" | "first_name" | "last_name"> | undefined): string {
  if (!t) return "–";
  return t.display_name ?? `${t.first_name} ${t.last_name ?? ""}`.trim();
}

/** Kurze Beträge für Achsen und Säulen wie in der Vorlage („18,4k“). */
export function kurz(cents: number): string {
  return shortAmount(cents);
}

// ── Zeiträume (AD-017) ─────────────────────────────────────────────────────

export type ZeitraumKey = "monat" | "vormonat" | "quartal" | "6m" | "ytd" | "12m" | "24m" | "vorjahr" | "alle" | "eigen";
export const ZEITRAEUME: readonly { key: ZeitraumKey; label: string }[] = [
  { key: "monat", get label() { return t("Aktueller Monat", "Current month"); } },
  { key: "vormonat", get label() { return t("Letzter Monat", "Last month"); } },
  { key: "quartal", get label() { return t("Laufendes Quartal", "Current quarter"); } },
  { key: "6m", get label() { return t("Letzte 6 Monate", "Last 6 months"); } },
  { key: "ytd", get label() { return t("Laufendes Jahr", "Current year"); } },
  { key: "12m", get label() { return t("Letzte 12 Monate", "Last 12 months"); } },
  { key: "24m", get label() { return t("Letzte 24 Monate", "Last 24 months"); } },
  { key: "vorjahr", get label() { return t("Vorjahr", "Previous year"); } },
  { key: "alle", get label() { return t("Gesamter Zeitraum", "Whole period"); } },
  { key: "eigen", get label() { return t("Eigener Zeitraum", "Custom period"); } },
];

// Monatsnamen in der Oberflächensprache (lib/sprache.ts)
export const monatLang = (m: string) => `${monthNames()[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
export const monatName = (m: string) => monthNames()[Number(m.slice(5, 7)) - 1] ?? m;
export const monatKurz = (m: string) => monthNames(undefined, true)[Number(m.slice(5, 7)) - 1] ?? m;

function spanne(from: string, to: string): string {
  if (from === to) return monatLang(from);
  return from.slice(0, 4) === to.slice(0, 4) ? `${monatName(from)}–${monatName(to)} ${to.slice(0, 4)}` : `${monatLang(from)} – ${monatLang(to)}`;
}

/** Zeitraum als Monatsbereich; „Gesamter Zeitraum“ reicht vom frühesten bis zum spätesten Monat mit Daten. */
/** Grenzen des eigenen Zeitraums (Praxistest K-150): „von 0001-01“ erzeugte 25.200 Monate und fror die Seite ein */
export const EIGEN_MIN = "2000-01";
export const EIGEN_MAX_MONATE = 120;
export const eigenMax = (heute: string) => `${Number(heute.slice(0, 4)) + 10}-12`;

export function zeitraum(key: ZeitraumKey, heute: string, opts: { von?: string; bis?: string; fruehester?: string | null; spaetester?: string | null } = {}): { from: string; to: string; label: string; /** eigener Zeitraum wurde auf die erlaubte Spanne gekürzt */ gekuerzt?: boolean } {
  const cur = heute.slice(0, 7);
  const jahr = cur.slice(0, 4);
  const r = (from: string, to: string) => ({ from, to, label: spanne(from, to) });
  switch (key) {
    case "monat": return r(cur, cur);
    case "vormonat": return r(addMonths(cur, -1), addMonths(cur, -1));
    case "quartal": {
      const q0 = `${jahr}-${String(Math.floor((Number(cur.slice(5, 7)) - 1) / 3) * 3 + 1).padStart(2, "0")}`;
      return r(q0, addMonths(q0, 2));
    }
    case "ytd": return r(`${jahr}-01`, cur);
    case "12m": return r(addMonths(cur, -11), cur);
    case "24m": return r(addMonths(cur, -23), cur);
    case "vorjahr": {
      const y = String(Number(jahr) - 1);
      return r(`${y}-01`, `${y}-12`);
    }
    case "alle": {
      const from = opts.fruehester && opts.fruehester < cur ? opts.fruehester : cur;
      const to = opts.spaetester && opts.spaetester > cur ? opts.spaetester : cur;
      // Ohne Datenspanne (Auswahlliste) nur der Name, sonst die echte Spanne (Befund 19)
      return { from, to, label: opts.fruehester || opts.spaetester ? t(`Gesamter Zeitraum (${spanne(from, to)})`, `Whole period (${spanne(from, to)})`) : t("Gesamter Zeitraum", "Whole period") };
    }
    case "eigen": {
      const ok = (m?: string) => !!m && /^\d{4}-(0[1-9]|1[0-2])$/.test(m);
      let from = ok(opts.von) ? opts.von! : addMonths(cur, -5);
      let to = ok(opts.bis) ? opts.bis! : from > cur ? from : cur;
      // „bis“ vor „von“ (K-154 e): die beiden Monate tauschen, statt still auf einen Monat zu springen
      if (to < from) [from, to] = [to, from];
      // Grenzen (K-150): nicht vor 2000, höchstens zehn Jahre voraus, höchstens 120 Monate am Stück
      const max = eigenMax(heute);
      let gekuerzt = false;
      if (from < EIGEN_MIN) { from = EIGEN_MIN; gekuerzt = true; }
      if (to > max) { to = max; gekuerzt = true; }
      if (to < from) to = from;
      if (addMonths(from, EIGEN_MAX_MONATE - 1) < to) { to = addMonths(from, EIGEN_MAX_MONATE - 1); gekuerzt = true; }
      return gekuerzt ? { ...r(from, to), gekuerzt } : r(from, to);
    }
    default: return r(addMonths(cur, -5), cur);
  }
}

// ── Umrechnung (AD-018, KZ-14) ─────────────────────────────────────────────

export type Umrechner = (cents: number, von: string, nach: string, monat: string) => number | null;

/** Letzter Kurs bis Monatsende, direkt, umgekehrt oder über den Euro (wie app.fx_rate und die Übersicht); null ohne Kurs. */
export function umrechner(rates: readonly KzFx[]): Umrechner {
  return fxMonthConverter(rates);
}

/**
 * Buchungen mit eingefrorenem Kurs (KZ-14): in der Agenturwährung zählt der beim Buchen festgehaltene Betrag, nicht ein
 * späterer Kurs. Für die Rechnung wird die Buchung vorab in die Agenturwährung gesetzt.
 */
export function mitEingefrorenemKurs(b: KzBooking, anzeige: string, orgWaehrung: string): KzBooking {
  if (b.currency === anzeige || anzeige !== orgWaehrung || b.amount_org_cents == null || !b.fx_rate_to_org) return b;
  const f = Number(b.fx_rate_to_org);
  return {
    ...b, currency: orgWaehrung, amount_cents: b.amount_org_cents,
    agency_share_cents: b.agency_share_cents == null ? null : Math.round(b.agency_share_cents * f),
    talent_share_cents: b.talent_share_cents == null ? null : b.amount_org_cents - Math.round((b.agency_share_cents ?? 0) * f),
  };
}

// ── Rechenkern: Posten je Monat ────────────────────────────────────────────

export function feeAus(mode: string | null, bp: number | null, cents: number | null): OverviewFee {
  if (mode === "percent" && bp != null) return { mode: "percent", percent: bp / 100 };
  if (mode === "fixed" && cents != null) return { mode: "fixed", cents };
  return { mode: "none" };
}

/** Eingabe für computeOverview wie die Übersicht (uebersicht/data.ts), damit beide Seiten dieselben Zahlen zeigen. */
export function overviewEingabe(d: KennzahlenDaten, a: { heute: string; waehrung: string; talentId: string | null; convert: Umrechner }): OverviewInput {
  const s = d.settings;
  const terms = new Map(d.terms.map((t) => [t.deal_id, t]));
  const brandRoute = new Map(d.brands.map((b) => [b.id, b.invoice_route ?? null]));
  // Umsatzbeginn nach Einstellung (0047, 0060): Arbeitsstand oder unterschriebener Vertrag; sonst nur verbuchte Monate
  const signed = unterschriebeneKooperationen(d);
  const linkedPeriods = new Map<string, string[]>();
  for (const l of d.invoicePeriods ?? []) linkedPeriods.set(l.invoice_id, [...(linkedPeriods.get(l.invoice_id) ?? []), l.period_id]);
  const org = s?.currency ?? a.waehrung;
  const cur = a.heute.slice(0, 7);
  return {
    today: a.heute, from: cur, to: cur, talentId: a.talentId, currency: a.waehrung, orgCurrency: org,
    agencyCommissionPercent: (s?.default_commission_bp ?? 0) / 100,
    talentCommissionPercent: Object.fromEntries(d.talents.map((t) => [t.id, t.commission_bp == null ? null : t.commission_bp / 100])),
    agencyFee: feeAus(s?.agency_fee_mode ?? null, s?.agency_fee_bp ?? null, s?.agency_fee_cents ?? null),
    // Eine Umwandlung mit der Übersicht (overviewDealFromRow): Weg, Umsatzbeginn, Staffel, Vermittler, Abzug der Marke
    deals: d.deals.map((x) => {
      const t = terms.get(x.id);
      return overviewDealFromRow(x, { fee: t ? feeAus(t.agency_fee_mode, t.agency_fee_bp, t.agency_fee_cents) : null, terms: t ?? null,
        brandRoute: brandRoute.get(x.brand_id) ?? null, orgRoute: s?.invoice_route ?? null, revenueStart: s?.revenue_start ?? null, signed: signed.has(x.id) });
    }),
    // Stornierte Monate zählen nie, Ausfallhonorare mit ihrem Betrag (Befund 1)
    periods: d.periods.map((p) => overviewPeriodFromRow(p)),
    extras: d.extras.map((e) => ({ dealId: e.deal_id, periodId: e.period_id, amountCents: e.amount_cents, commissionable: e.commissionable, createdAt: e.created_at ?? null })),
    invoices: d.invoices.map((i) => overviewInvoiceFromRow(i, linkedPeriods.get(i.id))),
    bookings: d.bookings.map((raw) => {
      const b = mitEingefrorenemKurs(raw, a.waehrung, org);
      return {
        id: b.id, dealId: b.deal_id, periodId: b.period_id, talentId: b.talent_id, brandId: b.brand_id, kind: b.kind, bookedOn: b.booked_on, amountCents: b.amount_cents,
        currency: b.currency, agencyShareCents: b.agency_share_cents, status: b.status,
      };
    }),
    convert: a.convert,
  };
}

/** Posten mit Creator-Anteil: bei „Nur Provision“ ist der Creator-Anteil unbekannt (die Marke zahlt den Creator direkt). */
export interface Posten extends Occurrence {
  creatorCents: number;
}

export function posten(d: KennzahlenDaten, a: { heute: string; waehrung: string; talentId: string | null; convert: Umrechner }): { posten: Posten[]; ohneKurs: number } {
  const o = computeOverview(overviewEingabe(d, a));
  const modelle = new Map(d.deals.map((x) => [x.id, x.model]));
  return {
    // Creator-Anteil = was die Marke für Honorar und Zusatz zahlt (nach Abzug der Marke) minus Agenturanteil
    posten: o.occurrences.map((p) => ({
      ...p,
      creatorCents: p.source === "deal" && modelle.get(p.dealId) === "commission_only" ? 0 : p.budgetCents + p.extraCents - p.discountCents - p.agencyShareCents,
    })),
    ohneKurs: o.unconverted,
  };
}

// ── Summen und Aufschlüsselung ─────────────────────────────────────────────

export interface Summe {
  umsatz: number;
  creator: number;
  provision: number;
  gebuehren: number;
  agentur: number;
  verbucht: number;
  vorlaeufig: number;
  posten: number;
  kooperationen: number;
  /** Vermittlerprovision (Aufwand der Agentur) und Agentur netto danach (Befund 4) */
  vermittler: number;
  nachVermittler: number;
}

export function summiere(list: readonly Posten[]): Summe {
  const koops = new Set<string>();
  const s: Summe = { umsatz: 0, creator: 0, provision: 0, gebuehren: 0, agentur: 0, verbucht: 0, vorlaeufig: 0, posten: list.length, kooperationen: 0, vermittler: 0, nachVermittler: 0 };
  for (const p of list) {
    s.umsatz += p.grossCents;
    s.creator += p.creatorCents;
    s.provision += p.agencyShareCents;
    s.gebuehren += p.feeCents;
    s.agentur += p.managementCents;
    s.vermittler += p.referralCents;
    s.nachVermittler += p.managementCents - p.referralCents;
    if (p.booked) s.verbucht += p.managementCents;
    else s.vorlaeufig += p.managementCents;
    if (p.source === "deal") koops.add(p.dealId);
  }
  s.kooperationen = koops.size;
  return s;
}

export interface Zeile {
  key: string;
  summe: Summe;
  vorjahrAgentur: number;
  /** Anteil am Agenturumsatz des Zeitraums in Prozent */
  anteil: number;
}

export interface Auswertung {
  from: string;
  to: string;
  summe: Summe;
  vorjahr: Summe;
  monate: { month: string; summe: Summe; vorjahr: Summe }[];
  nachCreator: Zeile[];
  nachMarke: Zeile[];
  nachPlattform: Zeile[];
  /** Herkunft der Kooperation (0074): creator, agency, brand, none (keine Angabe), booking (Buchung ohne Kooperation) */
  nachHerkunft: Zeile[];
  aktuellerMonat: { month: string; agentur: number; vormonat: number };
}

const imBereich = (m: string, from: string, to: string) => m >= from && m <= to;

function gruppiere(list: readonly Posten[], vj: readonly Posten[], key: (p: Posten) => string, gesamt: number): Zeile[] {
  const jetzt = new Map<string, Posten[]>();
  for (const p of list) jetzt.set(key(p), [...(jetzt.get(key(p)) ?? []), p]);
  const vorher = new Map<string, number>();
  for (const p of vj) vorher.set(key(p), (vorher.get(key(p)) ?? 0) + p.managementCents);
  return [...jetzt.entries()]
    .map(([k, ps]) => {
      const summe = summiere(ps);
      return { key: k, summe, vorjahrAgentur: vorher.get(k) ?? 0, anteil: gesamt ? (summe.agentur / gesamt) * 100 : 0 };
    })
    .sort((a, b) => b.summe.agentur - a.summe.agentur || b.summe.umsatz - a.summe.umsatz);
}

/** Posten auf die Plattformen verteilen, zu gleichen Teilen (KZ-17); Buchungen und Kooperationen ohne Plattform eigene Gruppen. */
export function aufPlattformen(list: readonly Posten[]): Posten[] {
  const out: Posten[] = [];
  for (const p of list) {
    const plats = p.platforms.length ? p.platforms : [p.source === "booking" ? "manual" : "other"];
    const n = plats.length;
    plats.forEach((pl, i) => {
      // Rundungsrest auf die erste Plattform, damit die Summen aufgehen
      const teil = (v: number) => Math.trunc(v / n) + (i === 0 ? v - Math.trunc(v / n) * n : 0);
      out.push({
        ...p, platforms: [pl], budgetCents: teil(p.budgetCents), extraCents: teil(p.extraCents), agencyShareCents: teil(p.agencyShareCents),
        feeCents: teil(p.feeCents), managementCents: teil(p.managementCents), grossCents: teil(p.grossCents), creatorCents: teil(p.creatorCents),
        referralCents: teil(p.referralCents), discountCents: teil(p.discountCents),
      });
    });
  }
  return out;
}

/** Schlüssel der Herkunft je Posten aus der Kooperation; Buchungen ohne Kooperation eigene Gruppe. */
export function herkunftSchluessel(deals: readonly Pick<KzDeal, "id" | "origin">[]): (p: Pick<Posten, "dealId" | "source">) => string {
  const m = new Map(deals.map((d) => [d.id, d.origin ?? null]));
  return (p) => (!p.dealId || !m.has(p.dealId) ? (p.source === "booking" ? "booking" : "none") : m.get(p.dealId) ?? "none");
}

export function auswerten(alle: readonly Posten[], a: { from: string; to: string; heute: string; brandId: string | null; herkunft?: (p: Posten) => string }): Auswertung {
  const list = a.brandId ? alle.filter((p) => p.brandId === a.brandId) : alle;
  const range = list.filter((p) => imBereich(p.month, a.from, a.to));
  const pyFrom = addMonths(a.from, -12), pyTo = addMonths(a.to, -12);
  const vj = list.filter((p) => imBereich(p.month, pyFrom, pyTo));
  const summe = summiere(range);
  const cur = a.heute.slice(0, 7);
  const agenturIm = (m: string) => list.filter((p) => p.month === m).reduce((s, p) => s + p.managementCents, 0);
  return {
    from: a.from, to: a.to, summe, vorjahr: summiere(vj),
    monate: monthRange(a.from, a.to).map((m) => ({ month: m, summe: summiere(range.filter((p) => p.month === m)), vorjahr: summiere(vj.filter((p) => p.month === addMonths(m, -12))) })),
    nachCreator: gruppiere(range, vj, (p) => p.talentId, summe.agentur),
    nachMarke: gruppiere(range, vj, (p) => p.brandId, summe.agentur),
    nachPlattform: gruppiere(aufPlattformen(range), aufPlattformen(vj), (p) => p.platforms[0] ?? "other", summe.agentur),
    nachHerkunft: gruppiere(range, vj, a.herkunft ?? (() => "none"), summe.agentur),
    aktuellerMonat: { month: cur, agentur: agenturIm(cur), vormonat: agenturIm(addMonths(cur, -1)) },
  };
}

/** Veränderung in Prozent; null ohne Vergleichswert. */
export function veraenderung(jetzt: number, vorher: number): number | null {
  return vorher > 0 ? ((jetzt - vorher) / vorher) * 100 : null;
}

// ── Offene Forderungen (Vorlage: „Offene Forderungen · An Marken · brutto“) ──

export interface Forderungen {
  offen: number;
  anzahlOffen: number;
  ueberfaellig: number;
  anzahlUeberfaellig: number;
  /** Freigegeben, aber noch nicht versendet: keine Forderung mit Fälligkeit */
  zuVersenden: number;
  anzahlZuVersenden: number;
  ohneKurs: number;
}

/**
 * Offene, versendete Rechnungen an Marken brutto, wie die Altersstruktur der Übersicht; freigegebene, nicht versendete
 * getrennt. Nach Teilzahlungen zählt nur der offene Rest (PRAXIS-5). Andere Währungen mit dem eingefrorenen Betrag der
 * Rechnung (Agenturwährung, anteilig) oder dem hinterlegten Kurs; ohne Kurs gezählt, nicht addiert.
 */
export function forderungen(invoices: readonly KzInvoice[], a: {
  heute: string; waehrung: string; orgWaehrung: string; convert: Umrechner; talentId: string | null; brandId: string | null; dealBrand: (dealId: string | null) => string | null;
}): Forderungen {
  const f: Forderungen = { offen: 0, anzahlOffen: 0, ueberfaellig: 0, anzahlUeberfaellig: 0, zuVersenden: 0, anzahlZuVersenden: 0, ohneKurs: 0 };
  for (const i of invoices) {
    if (i.kind === "talent_to_agency" || !["approved", "sent"].includes(i.status)) continue;
    if (a.talentId && i.talent_id !== a.talentId) continue;
    if (a.brandId && (i.brand_id ?? a.dealBrand(i.deal_id)) !== a.brandId) continue;
    if ((i.gross_cents ?? 0) > 0 && (i.paid_cents ?? 0) >= (i.gross_cents ?? 0)) continue;
    // Praxistest K-124: Stornorechnungen und Gutschriften (Betrag null oder negativ) sind keine Forderung; sie zählten als
    // „offene Rechnung“ mit (zweite Rechnung in der Zählung, obwohl nur eine offen war)
    if ((i.gross_cents ?? 0) <= 0) continue;
    const betrag = invoiceOpenAmountIn({ currency: i.currency, grossCents: i.gross_cents, amountOrgCents: i.amount_org_cents, issueDate: i.issue_date,
      revenueMonth: null, paidCents: i.paid_cents ?? 0 }, a.waehrung, { today: a.heute, orgCurrency: a.orgWaehrung, convert: a.convert });
    if (betrag === null) {
      f.ohneKurs += 1;
      continue;
    }
    if (i.status === "approved") {
      f.zuVersenden += betrag;
      f.anzahlZuVersenden += 1;
      continue;
    }
    f.offen += betrag;
    f.anzahlOffen += 1;
    const faellig = i.effective_due_date ?? i.document_due_date;
    // Rückläufer bzw. noch nicht zugegangen: keine Frist, nicht überfällig (GELD-R3-7, wie Rechnungsseite und Liste)
    if (faellig && faellig < a.heute && !rowAwaitsDelivery(i)) {
      f.ueberfaellig += betrag;
      f.anzahlUeberfaellig += 1;
    }
  }
  return f;
}

// ── Kooperationen und Pipeline ─────────────────────────────────────────────

/** „Aktiv“ wie in der Liste der Kooperationen: nicht archiviert, nicht storniert, nicht abgeschlossen (Befund 21: Kennzahl heißt „Laufende Kooperationen“) */
const INAKTIV = new Set(["done", "cancelled"]);
const passt = (d: KzDeal, talentId: string | null, brandId: string | null) =>
  !d.archived_at && !INAKTIV.has(d.status) && (!talentId || d.talent_id === talentId) && (!brandId || d.brand_id === brandId);

export function aktiveKooperationen(deals: readonly KzDeal[], a: { talentId: string | null; brandId: string | null }): { anzahl: number; creator: number; marken: number } {
  const list = deals.filter((d) => passt(d, a.talentId, a.brandId));
  return { anzahl: list.length, creator: new Set(list.map((d) => d.talent_id)).size, marken: new Set(list.map((d) => d.brand_id)).size };
}

/** Beschriftung der Stufen; das Schema selbst (PIPELINE_STAGES) teilen Übersicht und Kennzahlen im Rechenkern. */
export const PIPELINE_BEZEICHNUNG: Record<string, string> = {
  get planning() { return t("Planung", "Planning"); }, get contract() { return t("Vertrag", "Contract"); }, get production() { return t("Produktion", "Production"); }, get invoicing() { return t("Abrechnung", "Billing"); }, get blocked() { return t("Blockiert", "Blocked"); },
};

/**
 * Offenes Honorarvolumen netto der laufenden Kooperationen je Stufe (ohne Zeitraumfilter): nur Monate, die noch nicht in
 * Rechnung gestellt oder gebucht sind; Modelle ohne Geld zählen als Kooperation ohne Betrag. Dieselbe Rechnung wie die Übersicht.
 */
export function pipeline(d: Pick<KennzahlenDaten, "deals" | "periods" | "invoices" | "invoicePeriods">, a: {
  waehrung: string; heute: string; convert: Umrechner; talentId: string | null; brandId: string | null;
}): { stufen: { key: string; label: string; cents: number; anzahl: number; ohneGeld: number }[]; gesamt: number; ohneKurs: number } {
  const linked = new Map<string, string[]>();
  for (const l of d.invoicePeriods ?? []) linked.set(l.invoice_id, [...(linked.get(l.invoice_id) ?? []), l.period_id]);
  const r = computePipeline({
    today: a.heute, currency: a.waehrung, convert: a.convert, talentId: a.talentId, brandId: a.brandId,
    deals: d.deals.map((x) => ({ id: x.id, talentId: x.talent_id, brandId: x.brand_id, model: x.model, status: x.status, currency: x.currency, commissionPercent: null, fee: null,
      archived: !!x.archived_at, budgetCents: x.budget_cents })),
    // Stornierte Monate und per Sammelrechnung abgerechnete zählen nicht mehr (Befunde 1, 5)
    periods: d.periods.map((p) => overviewPeriodFromRow(p)),
    // Dieselbe Umwandlung wie die Übersicht, mit Nettobetrag: ohne ihn zog die Pipeline Rechnungen ohne Monat nicht ab (GELD-R3-8)
    invoices: d.invoices.map((i) => overviewInvoiceFromRow(i, linked.get(i.id))),
  });
  const stufen = r.stages.map((s) => ({ key: s.key, label: PIPELINE_BEZEICHNUNG[s.key] ?? s.key, cents: s.budgetCents, anzahl: s.deals, ohneGeld: s.noMoneyDeals }));
  return { stufen, gesamt: stufen.reduce((s, x) => s + x.cents, 0), ohneKurs: r.unconverted };
}

// ── Buchungen (Ledger, AD-033 … AD-036) ────────────────────────────────────

export type LedgerArt = "budget" | "zusatz" | "gebuehr" | "provision" | "offen" | "manuell" | "abzug" | "vermittler";
export const LEDGER_ARTEN: readonly { key: LedgerArt | "alle"; label: string }[] = [
  { key: "alle", get label() { return t("Alle Positionen", "All items"); } },
  { key: "budget", get label() { return t("Kooperationsbudget", "Deal budget"); } },
  { key: "zusatz", get label() { return t("Zusatz und Nutzungsrechte", "Extras and usage rights"); } },
  { key: "gebuehr", get label() { return t("Agenturgebühren", "Agency fees"); } },
  { key: "provision", get label() { return t("Provision", "Commission"); } },
  { key: "offen", get label() { return t("Offene Provisionen", "Open commissions"); } },
  { key: "manuell", get label() { return t("Manuelle Buchungen", "Manual bookings"); } },
  { key: "abzug", get label() { return t("Agenturvergütung der Marke", "Brand agency compensation"); } },
  { key: "vermittler", get label() { return t("Vermittlerprovisionen", "Referral commissions"); } },
];
export const LEDGER_LABEL: Record<LedgerArt, string> = {
  get budget() { return t("Kooperationsbudget", "Deal budget"); }, get zusatz() { return t("Zusatz und Nutzungsrechte", "Extras and usage rights"); }, get gebuehr() { return t("Agenturgebühr", "Agency fee"); }, get provision() { return t("Provision", "Commission"); }, get offen() { return t("Offene Provision", "Open commission"); }, get manuell() { return t("Manuelle Buchung", "Manual booking"); },
  get abzug() { return t("Agenturvergütung der Marke", "Brand agency compensation"); }, get vermittler() { return t("Vermittlerprovision", "Referral commission"); },
};
/** Keine Umsatzposten: Abzug der Marke und Vermittlerprovision stehen zur Nachvollziehbarkeit, zählen aber nicht im Umsatz */
const OHNE_UMSATZ = new Set<LedgerArt>(["abzug", "vermittler"]);
export type LedgerStand = "verbucht" | "vorlaeufig" | "geplant" | "storniert";
export const LEDGER_STAENDE: readonly { key: LedgerStand | "alle"; label: string }[] = [
  { key: "alle", get label() { return t("Alle Stände", "All statuses"); } },
  { key: "verbucht", get label() { return t("Verbucht", "Booked"); } },
  { key: "vorlaeufig", get label() { return t("Vorläufig", "Provisional"); } },
  { key: "geplant", get label() { return t("Geplant", "Planned"); } },
  { key: "storniert", get label() { return t("Storniert", "Cancelled"); } },
];

export interface LedgerZeile {
  key: string;
  monat: string;
  datum: string | null;
  art: LedgerArt;
  label: string;
  dealId: string | null;
  talentId: string | null;
  brandId: string | null;
  plattformen: string[];
  /** Betrag in Originalwährung; null = kein Betrag hinterlegt */
  betrag: number | null;
  waehrung: string;
  /** Betrag in der Anzeigewährung; null ohne Kurs */
  umgerechnet: number | null;
  /** Agenturanteil dieser Zeile in der Anzeigewährung */
  agentur: number | null;
  stand: LedgerStand;
  pille: Pill;
  /** Zählt die Zeile im Umsatz (Buchung zu einer Kooperation mit eigenem Umsatz nicht, KZ-13) */
  imUmsatz: boolean;
  hinweis: string | null;
  buchung?: KzBooking;
}

const STAND_PILLE: Record<LedgerStand, Pill> = {
  get verbucht() { return pill(t("Verbucht", "Booked"), "success"); }, get vorlaeufig() { return pill(t("Vorläufig", "Provisional"), "info"); }, get geplant() { return pill(t("Geplant", "Planned"), "warning"); }, get storniert() { return pill(t("Storniert", "Cancelled"), "neutral"); },
};

/**
 * Herkunft des Ist (GELD-26): über die Agentur die Rechnung des Creators, im Direktweg die Provisionsrechnung der Agentur an den
 * Creator, bei „Nur Provision“ die Rechnung der Agentur; ohne Ist null.
 */
export function istHerkunft(o: { actual: boolean; actualFrom?: "creator_invoice" | "agency_invoice"; source?: string }, modell?: string | null): string | null {
  if (!o.actual || o.source === "booking") return null;
  if (o.actualFrom === "agency_invoice") return modell === "commission_only" ? t("Ist aus Agenturrechnung", "Actual from agency invoice") : t("Ist aus Provisionsrechnung", "Actual from commission invoice");
  return t("Ist aus Creator-Rechnung", "Actual from creator invoice");
}

/** Alle Positionen: Kooperationsmonate (Budget, Zusatz, Gebühr, Provision) und manuelle Buchungen, in Originalwährung und umgerechnet. */
export function ledger(d: KennzahlenDaten, a: { heute: string; waehrung: string; orgWaehrung: string; convert: Umrechner }): LedgerZeile[] {
  const deals = new Map(d.deals.map((x) => [x.id, x]));
  const periodeById = new Map(d.periods.map((p) => [p.id, p]));
  // Originalwährung: Umrechnung abschalten (jede Währung gilt als „fremd“, die Umrechnung gibt den Betrag unverändert zurück)
  const roh = dealOccurrences({ ...overviewEingabe({ ...d, bookings: [] }, { heute: a.heute, waehrung: "__", talentId: null, convert: a.convert }), convert: (c) => c });
  const out: LedgerZeile[] = [];
  const conv = (c: number, w: string, m: string) => a.convert(c, w, a.waehrung, m);
  for (const o of roh) {
    const deal = deals.get(o.dealId);
    if (!deal) continue;
    const w = deal.currency;
    const stand: LedgerStand = o.booked ? "verbucht" : "vorlaeufig";
    const basis = {
      monat: o.month, datum: null, dealId: o.dealId, talentId: o.talentId, brandId: o.brandId, plattformen: [...o.platforms], waehrung: w, stand,
      pille: STAND_PILLE[stand], imUmsatz: true, hinweis: o.cancellationFee ? t("Ausfallhonorar", "Cancellation fee") : istHerkunft(o, deal.model),
    };
    const zeile = (art: LedgerArt, betrag: number | null, agentur: number | null, extra: Partial<LedgerZeile> = {}): LedgerZeile => ({
      ...basis, key: `${o.periodId}:${art}`, art, label: LEDGER_LABEL[art], betrag, umgerechnet: betrag === null ? null : conv(betrag, w, o.month),
      agentur: agentur === null ? null : conv(agentur, w, o.month), ...extra,
    });
    if (deal.model === "commission_only") {
      out.push(o.agencyShareCents > 0
        ? zeile("provision", o.agencyShareCents, o.agencyShareCents, { hinweis: o.actual ? t("Ist aus Agenturrechnung", "Actual from agency invoice") : t("Erwartete Provision", "Expected commission") })
        : zeile("offen", null, null, { hinweis: t("Noch kein Provisionsbetrag", "No commission amount yet") }));
    } else {
      // Fest + Provision: die erwartete Provision steht als eigene Zeile, der Anteil am Festbetrag beim Budget
      const provision = deal.model === "fixed_plus_commission" ? (periodeById.get(o.periodId)?.commission_forecast_cents ?? 0) : 0;
      const anteilFest = o.agencyShareCents - provision;
      let anteilGezeigt = false;
      if (o.budgetCents !== 0 || o.extraCents === 0) {
        out.push(zeile("budget", o.budgetCents || null, anteilFest, o.budgetCents ? {} : { hinweis: t("Kein Betrag hinterlegt", "No amount on file") }));
        anteilGezeigt = true;
      }
      if (o.extraCents !== 0) out.push(zeile("zusatz", o.extraCents, anteilGezeigt ? null : anteilFest));
      if (provision) out.push(zeile("provision", provision, provision, { hinweis: t("Erwartete Provision", "Expected commission") }));
    }
    if (o.feeCents !== 0) out.push(zeile("gebuehr", o.feeCents, o.feeCents));
    if (o.discountCents) out.push(zeile("abzug", -o.discountCents, null, { imUmsatz: false, hinweis: t("Die Marke zahlt weniger (AE-Provision, Agenturrabatt)", "The brand pays less (AE commission, agency discount)") }));
    if (o.referralCents) out.push(zeile("vermittler", -o.referralCents, null, { imUmsatz: false, hinweis: t("Aufwand der Agentur, im Agenturanteil nicht abgezogen", "Agency expense, not deducted in the agency share") }));
  }
  const gezaehlteMonate = new Set(roh.map((o) => o.periodId));
  const org = a.orgWaehrung;
  const standard = (d.settings?.default_commission_bp ?? 0) / 100;
  for (const b of d.bookings) {
    const stand: LedgerStand = b.status === "booked" ? "verbucht" : b.status === "cancelled" ? "storniert" : "geplant";
    const frozen = mitEingefrorenemKurs(b, a.waehrung, org);
    const monat = b.booked_on.slice(0, 7);
    const umgerechnet = conv(frozen.amount_cents, frozen.currency, monat);
    const anteil = b.status === "cancelled" ? 0 : b.kind === "agency_fee" ? frozen.amount_cents
      : b.kind === "expense" ? frozen.agency_share_cents ?? 0 : frozen.agency_share_cents ?? Math.round((frozen.amount_cents * standard) / 100);
    const imUmsatz = bookingCounts({ status: b.status, amountCents: b.amount_cents, periodId: b.period_id }, gezaehlteMonate);
    const deal = b.deal_id ? deals.get(b.deal_id) : undefined;
    out.push({
      key: `booking:${b.id}`, monat, datum: b.booked_on, art: "manuell", label: BUCHUNGS_ART_LABEL[b.kind] ?? b.kind, dealId: b.deal_id,
      talentId: b.talent_id ?? deal?.talent_id ?? null, brandId: b.brand_id ?? deal?.brand_id ?? null, plattformen: [], betrag: b.amount_cents, waehrung: b.currency,
      umgerechnet, agentur: conv(anteil, frozen.currency, monat), stand, pille: b.status === "booked" ? pill(t("Gebucht", "Booked"), "success") : STAND_PILLE[stand], imUmsatz,
      hinweis: b.status === "cancelled" ? t("Storniert, zählt nicht", "Cancelled, does not count") : !imUmsatz ? t("Nicht im Umsatz: bildet einen Kooperationsmonat ab, der selbst zählt", "Not in revenue: represents a deal month that counts itself") : null,
      buchung: b,
    });
  }
  return out.sort((x, y) => y.monat.localeCompare(x.monat) || (x.datum ?? "").localeCompare(y.datum ?? "") || x.key.localeCompare(y.key));
}

export interface LedgerFilter {
  from: string;
  to: string;
  art: LedgerArt | "alle";
  stand: LedgerStand | "alle";
  talentId: string;
  brandId: string;
  suche: string;
  /** einzelner Monat (Klick im Diagramm) */
  monat?: string | null;
}

export function filtereLedger(rows: readonly LedgerZeile[], f: LedgerFilter, text: (z: LedgerZeile) => string): LedgerZeile[] {
  const q = f.suche.trim().toLowerCase();
  return rows.filter((z) => {
    if (f.monat ? z.monat !== f.monat : !imBereich(z.monat, f.from, f.to)) return false;
    if (f.art !== "alle" && z.art !== f.art) return false;
    if (f.stand !== "alle" && z.stand !== f.stand) return false;
    if (f.talentId && z.talentId !== f.talentId) return false;
    if (f.brandId && z.brandId !== f.brandId) return false;
    return !q || text(z).toLowerCase().includes(q);
  });
}

export interface LedgerSummen {
  positionen: number;
  monate: number;
  kooperationen: number;
  /** Summen je Art und Originalwährung (AD-034: Summen je Währung) */
  jeArt: Partial<Record<LedgerArt, Record<string, number>>>;
  umgerechnet: number;
  agentur: number;
  ohneKurs: number;
  offen: number;
}

export function ledgerSummen(rows: readonly LedgerZeile[]): LedgerSummen {
  const s: LedgerSummen = { positionen: rows.length, monate: new Set(rows.map((r) => r.monat)).size, kooperationen: new Set(rows.map((r) => r.dealId).filter(Boolean)).size,
    jeArt: {}, umgerechnet: 0, agentur: 0, ohneKurs: 0, offen: 0 };
  for (const r of rows) {
    if (r.art === "offen") s.offen += 1;
    if (r.stand === "storniert" || r.betrag === null) continue;
    const art = (s.jeArt[r.art] ??= {});
    art[r.waehrung] = (art[r.waehrung] ?? 0) + r.betrag;
    if (OHNE_UMSATZ.has(r.art)) continue;
    if (r.umgerechnet === null) s.ohneKurs += 1;
    else s.umgerechnet += r.umgerechnet;
    if (r.agentur !== null && r.imUmsatz) s.agentur += r.agentur;
  }
  return s;
}

// ── Buchung anlegen und ändern (AD-035) ────────────────────────────────────

export interface BuchungsFormular {
  art: BuchungsArtDb;
  datum: string;
  dealId: string;
  talentId: string;
  brandId: string;
  betrag: string;
  waehrung: string;
  /** Creator erhält einen Anteil (sonst alles an die Agentur) */
  creatorAnteil: boolean;
  agenturProzent: string;
  sichtbar: boolean;
  notiz: string;
  sofortBuchen: boolean;
}

export interface BuchungsDaten {
  kind: BuchungsArtDb;
  booked_on: string;
  deal_id: string | null;
  talent_id: string | null;
  brand_id: string | null;
  amount_cents: number;
  currency: string;
  talent_share_cents: number;
  agency_share_cents: number;
  visible_to_talent: boolean;
  note: string | null;
  status: "planned" | "booked";
}

function prozentText(v: number): string {
  return new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 2 }).format(v);
}

/**
 * Voreinstellung je Art: Provision und Agenturgebühr gehören ganz der Agentur, eine Auslage läuft durch (kein Agenturumsatz),
 * Nutzungsrechte, Bonus, Performance, Korrektur und Sonstiges werden nach Satz geteilt.
 */
export const ART_GANZ_AGENTUR = new Set<BuchungsArtDb>(["commission", "agency_fee"]);
export const ART_DURCHLAUFEND = new Set<BuchungsArtDb>(["expense"]);
export function creatorAnteilVorgabe(art: BuchungsArtDb): boolean {
  return !ART_GANZ_AGENTUR.has(art) && !ART_DURCHLAUFEND.has(art);
}

/** Satz der Agentur für eine neue Buchung: Kooperation (im Nachlauf je Monat des Buchungstags, 0074) → Creator → Agentur-Standard (WS-19, KZ-02). */
export function standardSatz(d: Pick<KennzahlenDaten, "deals" | "talents" | "settings">, dealId: string, talentId: string, datum?: string | null): number {
  const deal = d.deals.find((x) => x.id === dealId);
  const talent = d.talents.find((x) => x.id === (deal?.talent_id ?? talentId));
  const dealBp = deal ? dealBpForMonth(deal, datum) : null;
  return resolveCommissionRate({
    deal: dealBp == null ? null : dealBp / 100,
    talent: talent?.commission_bp == null ? null : talent.commission_bp / 100,
    agency: (d.settings?.default_commission_bp ?? 0) / 100,
  }).percent;
}

export function leereBuchung(datum: string, waehrung: string, satz: number): BuchungsFormular {
  return {
    art: "usage_rights", datum, dealId: "", talentId: "", brandId: "", betrag: "", waehrung, creatorAnteil: creatorAnteilVorgabe("usage_rights"),
    agenturProzent: prozentText(satz), sichtbar: false, notiz: "", sofortBuchen: false,
  };
}

export function formularAusBuchung(b: KzBooking): BuchungsFormular {
  const anteil = b.agency_share_cents ?? b.amount_cents;
  const prozent = b.amount_cents ? (anteil / b.amount_cents) * 100 : 100;
  return {
    art: b.kind, datum: b.booked_on, dealId: b.deal_id ?? "", talentId: b.talent_id ?? "", brandId: b.brand_id ?? "", betrag: (b.amount_cents / 100).toFixed(2).replace(".", ","),
    waehrung: b.currency, creatorAnteil: (b.talent_share_cents ?? 0) !== 0, agenturProzent: prozentText(prozent), sichtbar: b.visible_to_talent, notiz: b.note ?? "", sofortBuchen: false,
  };
}

function betragAus(text: string): number | null {
  try {
    return parseAmountToCents(text);
  } catch {
    return null;
  }
}

/**
 * Aufteilung wie beim Speichern: Agenturgebühr und „ohne Creator-Anteil“ gehen ganz an die Agentur; eine Auslage läuft
 * durch (Agenturanteil 0, zählt nicht als Agenturumsatz); sonst nach Satz.
 */
export function aufteilung(f: Pick<BuchungsFormular, "art" | "betrag" | "creatorAnteil" | "agenturProzent">): { agentur: number; creator: number } | null {
  const betrag = betragAus(f.betrag);
  if (betrag === null) return null;
  if (ART_DURCHLAUFEND.has(f.art)) return { agentur: 0, creator: betrag };
  if (f.art === "agency_fee" || !f.creatorAnteil) return { agentur: betrag, creator: 0 };
  const p = Number(f.agenturProzent.replace(",", "."));
  if (!Number.isFinite(p) || p < 0 || p > 100) return null;
  const agentur = Math.round((betrag * p) / 100);
  return { agentur, creator: betrag - agentur };
}

export function pruefeBuchung(f: BuchungsFormular, a: { darfBuchen: boolean }): { fehler: Record<string, string>; daten: BuchungsDaten | null } {
  const fehler: Record<string, string> = {};
  if (!isIsoDate(f.datum)) fehler.datum = t("Bitte ein gültiges Datum wählen.", "Please choose a valid date.");
  const betrag = betragAus(f.betrag);
  if (!f.betrag.trim()) fehler.betrag = t("Bitte einen Betrag eingeben.", "Please enter an amount.");
  else if (betrag === null) fehler.betrag = t("Bitte einen Betrag wie 1.200,00 eingeben.", "Please enter an amount like 1,200.00.");
  else if (betrag === 0) fehler.betrag = t("Der Betrag darf nicht 0 sein.", "The amount must not be 0.");
  if (!(CURRENCIES as readonly string[]).includes(f.waehrung)) fehler.waehrung = t("Bitte eine Währung wählen.", "Please choose a currency.");
  if (f.art !== "agency_fee" && !ART_DURCHLAUFEND.has(f.art) && f.creatorAnteil) {
    const p = Number(f.agenturProzent.replace(",", "."));
    if (!f.agenturProzent.trim() || !Number.isFinite(p) || p < 0 || p > 100) fehler.agenturProzent = t("Bitte einen Satz zwischen 0 und 100 eingeben.", "Please enter a rate between 0 and 100.");
    else if (p < 100 && !f.talentId && !f.dealId) fehler.creatorAnteil = t("Ein Creator-Anteil braucht einen Creator. Bitte Creator oder Kooperation wählen.", "A creator share needs a creator. Please choose a creator or deal.");
  }
  if (f.notiz.length > 2000) fehler.notiz = t("Die Notiz darf höchstens 2.000 Zeichen haben.", "The note may have at most 2,000 characters.");
  if (f.sofortBuchen && !a.darfBuchen) fehler.sofortBuchen = t("Buchen ist für dein Konto nicht freigegeben. Speichere die Buchung als geplant.", "Booking is not permitted for your account. Save the booking as planned.");
  if (f.sichtbar && !f.talentId && !f.dealId) fehler.sichtbar = t("Sichtbar für den Creator geht nur mit zugeordnetem Creator.", "Visible to the creator only works with an assigned creator.");
  const teil = aufteilung(f);
  if (Object.keys(fehler).length || betrag === null || !teil) return { fehler, daten: null };
  return {
    fehler,
    daten: {
      kind: f.art, booked_on: f.datum, deal_id: f.dealId || null, talent_id: f.talentId || null, brand_id: f.brandId || null, amount_cents: betrag, currency: f.waehrung,
      talent_share_cents: teil.creator, agency_share_cents: teil.agentur, visible_to_talent: f.sichtbar && !!(f.talentId || f.dealId), note: f.notiz.trim() || null,
      status: f.sofortBuchen ? "booked" : "planned",
    },
  };
}

// ── Export (Kennzahlen & Export) ───────────────────────────────────────────

/**
 * CSV für Excel (Semikolon, UTF-8 mit BOM), Felder mit Trennzeichen in Anführungszeichen. GB-W3: Schutz gegen Formeln in
 * Textzellen (Creator-, Marken-, Plattformnamen, Notizen); Beträge wie „-12,50“ bleiben Zahlen (csvCellGuard).
 */
export function toCsv(zeilen: readonly (readonly (string | number | null | undefined)[])[]): string {
  const feld = (v: string | number | null | undefined) => {
    const s = csvCellGuard(v === null || v === undefined ? "" : String(v));
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return "﻿" + zeilen.map((z) => z.map(feld).join(";")).join("\r\n");
}

/** Betrag für CSV ohne Tausendertrenner, Dezimalzeichen nach dem Zahlenformat der Agentur (de-DE/de-AT Komma, de-CH/en-GB Punkt). */
export function csvBetrag(cents: number | null | undefined, dezimal: string = decimalSeparator()): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return "";
  return (cents / 100).toFixed(2).replace(".", dezimal);
}

// ── Sachleistungen je Creator und Jahr (Warenwert, Praxisfall Steuer des Creators) ──

/**
 * Warenwert je Creator, Jahr und Währung aus den Kooperationsmonaten (stornierte Monate nicht). Pauschal von der Marke versteuerte
 * Produkte stehen getrennt: Sie sind beim Creator in der Regel nicht zu versteuern. Filter wie die übrige Seite.
 */
export function warenwertJeCreator(d: Pick<KennzahlenDaten, "deals" | "periods">, a: { talentId: string | null; brandId: string | null }): GoodsValueSummary[] {
  const deals = new Map(d.deals.map((x) => [x.id, x]));
  const rows: GoodsValueRow[] = [];
  for (const p of d.periods) {
    const deal = deals.get(p.deal_id);
    if (!deal || p.status === "cancelled" || !(p.goods_value_cents ?? 0)) continue;
    if ((a.talentId && deal.talent_id !== a.talentId) || (a.brandId && deal.brand_id !== a.brandId)) continue;
    rows.push({ talentId: deal.talent_id, dealId: deal.id, month: p.month.slice(0, 7), currency: deal.currency, goodsValueCents: p.goods_value_cents ?? 0, flatTaxed: !!deal.products_flat_taxed });
  }
  return goodsValueByTalentYear(rows);
}
